import React, { useState } from "react";
import { X, Plus, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * Per-widget comprehensive settings panel. Rendered inside a Popover or Sheet
 * by CanvasEditor when a widget element is selected. Lets the user edit ALL
 * configurable props: backgrounds (with transparent toggle), foreground color,
 * font size override, plus widget-specific content (city, items, target date, etc.).
 */
export default function WidgetSettings({ type, config = {}, onChange }) {
  const set = (k, v) => onChange({ ...config, [k]: v });
  const setMany = (patch) => onChange({ ...config, ...patch });

  return (
    <div className="space-y-4 text-sm" data-testid="widget-settings">
      {type === "weather"   && <WeatherSettings   config={config} set={set} setMany={setMany} />}
      {type === "clock"     && <ClockSettings     config={config} set={set} />}
      {type === "qr"        && <QRSettings        config={config} set={set} />}
      {type === "countdown" && <CountdownSettings config={config} set={set} />}
      {type === "ticker"    && <TickerSettings    config={config} set={set} setMany={setMany} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function Group({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">{title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center gap-3">
      <Label className="text-xs text-slate-600 flex-shrink-0 w-24">{label}</Label>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function ColorRow({ label, value, onChange, allowTransparent = true, defaultColor = "#FFFFFF" }) {
  const isTransparent = value === "transparent" || value === "" || value == null;
  return (
    <Row label={label}>
      <div className="flex items-center gap-2">
        {allowTransparent && (
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <input
              type="checkbox"
              checked={isTransparent}
              onChange={(e) => onChange(e.target.checked ? "transparent" : defaultColor)}
              className="accent-sky-500"
              data-testid={`${label.toLowerCase().replace(/\s+/g, "-")}-transparent`}
            />
            None
          </label>
        )}
        {!isTransparent && (
          <>
            <input
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="h-8 w-10 rounded border border-slate-200 cursor-pointer bg-white"
            />
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="h-8 text-xs"
            />
          </>
        )}
      </div>
    </Row>
  );
}

function NumberRow({ label, value, onChange, min = 0, max = 999, step = 1, suffix }) {
  return (
    <Row label={label}>
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          className="h-8 text-xs w-24"
        />
        {suffix && <span className="text-[11px] text-slate-500">{suffix}</span>}
      </div>
    </Row>
  );
}

function TextRow({ label, value, onChange, placeholder, testid }) {
  return (
    <Row label={label}>
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 text-xs"
        data-testid={testid}
      />
    </Row>
  );
}

function ToggleRow({ label, value, onChange, testid }) {
  return (
    <Row label={label}>
      <label className="flex items-center gap-2 text-xs text-slate-700">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="accent-sky-500"
          data-testid={testid}
        />
        Enabled
      </label>
    </Row>
  );
}

// ---------------------------------------------------------------------------
// Weather
// ---------------------------------------------------------------------------
function WeatherSettings({ config, set }) {
  return (
    <>
      <Group title="Location">
        <TextRow label="City" value={config.city} onChange={(v) => set("city", v)} placeholder="New York" testid="weather-city" />
        <Row label="Units">
          <Select value={config.unit || "fahrenheit"} onValueChange={(v) => set("unit", v)}>
            <SelectTrigger className="h-8 text-xs" data-testid="weather-units"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fahrenheit">°F (Fahrenheit)</SelectItem>
              <SelectItem value="celsius">°C (Celsius)</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      </Group>
      <Group title="Display">
        <Row label="View">
          <Select value={String(config.forecast_days || 1)} onValueChange={(v) => set("forecast_days", Number(v))}>
            <SelectTrigger className="h-8 text-xs" data-testid="weather-view"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today only</SelectItem>
              <SelectItem value="3">3-day forecast</SelectItem>
              <SelectItem value="7">7-day forecast</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        <ToggleRow label="Hi / Lo today" value={config.show_hilo !== false} onChange={(v) => set("show_hilo", v)} testid="weather-show-hilo" />
        <ToggleRow label="Realistic icon" value={config.realistic_icon !== false} onChange={(v) => set("realistic_icon", v)} testid="weather-realistic" />
      </Group>
      <Group title="Style">
        <ColorRow label="Background" value={config.bg || "linear-gradient(135deg, #38BDF8 0%, #0EA5E9 55%, #0284C7 100%)"} onChange={(v) => set("bg", v)} defaultColor="#0EA5E9" />
        <ColorRow label="Text" value={config.fg || "#FFFFFF"} onChange={(v) => set("fg", v)} allowTransparent={false} />
      </Group>
    </>
  );
}

// ---------------------------------------------------------------------------
// Clock
// ---------------------------------------------------------------------------
function ClockSettings({ config, set }) {
  return (
    <>
      <Group title="Format">
        <Row label="Time">
          <Select value={config.format_24h ? "24" : "12"} onValueChange={(v) => set("format_24h", v === "24")}>
            <SelectTrigger className="h-8 text-xs" data-testid="clock-format"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="12">12-hour (5:24 PM)</SelectItem>
              <SelectItem value="24">24-hour (17:24)</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        <ToggleRow label="Show seconds" value={config.show_seconds} onChange={(v) => set("show_seconds", v)} testid="clock-seconds" />
        <ToggleRow label="Show date"    value={config.show_date !== false} onChange={(v) => set("show_date", v)} testid="clock-date" />
      </Group>
      <Group title="Style">
        <ColorRow label="Background" value={config.bg || "#FFFFFF"} onChange={(v) => set("bg", v)} defaultColor="#FFFFFF" />
        <ColorRow label="Text" value={config.fg || "#0F172A"} onChange={(v) => set("fg", v)} allowTransparent={false} />
      </Group>
    </>
  );
}

// ---------------------------------------------------------------------------
// QR
// ---------------------------------------------------------------------------
function QRSettings({ config, set }) {
  return (
    <>
      <Group title="Content">
        <TextRow label="URL"   value={config.value} onChange={(v) => set("value", v)} placeholder="https://yoursite.com" testid="qr-value" />
        <TextRow label="Label" value={config.label} onChange={(v) => set("label", v)} placeholder="Scan for menu"        testid="qr-label" />
        <ToggleRow label="Show label" value={config.show_label !== false} onChange={(v) => set("show_label", v)} testid="qr-show-label" />
      </Group>
      <Group title="Style">
        <ColorRow label="Background" value={config.bg || "#FFFFFF"} onChange={(v) => set("bg", v)} defaultColor="#FFFFFF" />
        <ColorRow label="QR color"   value={config.qr_color || "#0F172A"} onChange={(v) => set("qr_color", v)} allowTransparent={false} />
        <ColorRow label="Label color" value={config.fg || "#0F172A"} onChange={(v) => set("fg", v)} allowTransparent={false} />
      </Group>
    </>
  );
}

// ---------------------------------------------------------------------------
// Countdown
// ---------------------------------------------------------------------------
function CountdownSettings({ config, set }) {
  // Convert ISO → local datetime-local string
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const fromLocalInput = (s) => s ? new Date(s).toISOString() : null;

  const units = config.units || ["days", "hours", "minutes", "seconds"];
  const toggleUnit = (u) => {
    const next = units.includes(u) ? units.filter(x => x !== u) : [...units, u];
    set("units", next);
  };

  return (
    <>
      <Group title="Target">
        <Row label="Ends at">
          <Input
            type="datetime-local"
            value={toLocalInput(config.target_iso)}
            onChange={(e) => set("target_iso", fromLocalInput(e.target.value))}
            className="h-8 text-xs"
            data-testid="countdown-target"
          />
        </Row>
        <TextRow label="Label" value={config.label} onChange={(v) => set("label", v)} placeholder="Sale ends in" testid="countdown-label" />
      </Group>
      <Group title="Display">
        <Row label="Show units">
          <div className="flex flex-wrap gap-1.5">
            {["days", "hours", "minutes", "seconds"].map((u) => (
              <button
                key={u}
                onClick={() => toggleUnit(u)}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${
                  units.includes(u)
                    ? "bg-sky-50 border-sky-300 text-sky-700"
                    : "bg-white border-slate-200 text-slate-500"
                }`}
                data-testid={`countdown-unit-${u}`}
              >
                {u}
              </button>
            ))}
          </div>
        </Row>
        <ToggleRow label="Show labels under numbers" value={config.show_unit_labels !== false} onChange={(v) => set("show_unit_labels", v)} testid="countdown-show-labels" />
      </Group>
      <Group title="Style">
        <ColorRow label="Background" value={config.bg || "#FFFFFF"} onChange={(v) => set("bg", v)} defaultColor="#FFFFFF" />
        <ColorRow label="Numbers" value={config.accent || "#0EA5E9"} onChange={(v) => set("accent", v)} allowTransparent={false} />
        <ColorRow label="Label" value={config.fg || "#0F172A"} onChange={(v) => set("fg", v)} allowTransparent={false} />
      </Group>
    </>
  );
}

// ---------------------------------------------------------------------------
// Ticker
// ---------------------------------------------------------------------------
function TickerSettings({ config, set, setMany }) {
  const items = config.items || [];
  const updateItem = (i, v) => set("items", items.map((it, idx) => idx === i ? v : it));
  const removeItem = (i) => set("items", items.filter((_, idx) => idx !== i));
  const addItem = () => set("items", [...items, "New message"]);
  const moveItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    set("items", next);
  };
  return (
    <>
      <Group title="Messages">
        <div className="space-y-1.5">
          {items.length === 0 && (
            <div className="text-xs text-slate-500 italic">No messages yet — add one below.</div>
          )}
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-1.5" data-testid={`ticker-item-${i}`}>
              <div className="flex flex-col text-slate-300">
                <button onClick={() => moveItem(i, -1)} className="text-[10px] hover:text-slate-600">▲</button>
                <button onClick={() => moveItem(i, 1)} className="text-[10px] hover:text-slate-600">▼</button>
              </div>
              <Input
                value={it}
                onChange={(e) => updateItem(i, e.target.value)}
                className="h-8 text-xs flex-1"
              />
              <button onClick={() => removeItem(i)} className="text-rose-500 hover:text-rose-700" data-testid={`ticker-remove-${i}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            onClick={addItem}
            className="w-full text-xs text-sky-600 hover:bg-sky-50 rounded-md py-1.5 border border-dashed border-sky-200 flex items-center justify-center gap-1"
            data-testid="ticker-add-item"
          >
            <Plus className="h-3 w-3" /> Add message
          </button>
        </div>
      </Group>
      <Group title="Behavior">
        <NumberRow label="Scroll speed" value={config.speed || 60} onChange={(v) => set("speed", v || 60)} min={5} max={300} suffix="seconds (lower = faster)" />
        <NumberRow label="Font size"    value={config.font_size_pct ?? 55} onChange={(v) => set("font_size_pct", v)} min={10} max={100} suffix="% of height" />
      </Group>
      <Group title="Style">
        <ColorRow label="Background" value={config.bg || "#0EA5E9"} onChange={(v) => set("bg", v)} defaultColor="#0EA5E9" />
        <ColorRow label="Text" value={config.fg || "#FFFFFF"} onChange={(v) => set("fg", v)} allowTransparent={false} />
      </Group>
    </>
  );
}
